import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {usePrevious} from '../../../../../../common/shared/hooks/usePrevious';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {events} from '../constant/talkViewerEvents';
import {useDisableScrollOnFullscreen} from '../hook/useDisableScrollOnFullscreen.hook';
import {useFocusTalk} from '../hook/useFocusTalk.hook';
import {useScrollWhenSceneChange} from '../hook/useScrollWhenSceneChange.hook';
import {FontLoadStatus} from '../type/FontLoadStatus.enum';
import {SceneKey, TalkKey} from '../type/KeyType.type';
import {TalkDataType} from '../type/TalkDataType.enum';
import {ViewingMode} from '../type/ViewingMode.enum';
import {scrollToWindowBottommost} from '../util/scrollToWindowBottommost';
import {TalkViewerTalkContainer} from './TalkViewerTalkContainer';

interface TalkViewerTalkListContainerProps {
  sceneId: SceneKey;
}

export function TalkViewerTalkListContainer({
  sceneId,
}: TalkViewerTalkListContainerProps) {
  const {
    talkHistory,
    viewingMode,
    nextSceneId,
    prevSceneId,
    talkMap,
    sceneMap,
    talkGroupMap,
    productInfo,
    fullscreen,
    isViewerLoaded,
    isViewerEndExposed,
    currentProductId,
    fontLoadStatus,
  } = useTypedSelector(state => {
    const {
      sceneInfoMap,
      settings: {viewingMode},
      currentProductId,
      productInfoMap,
      sceneMap,
      talkMap,
      talkGroupMap,
      fullscreen,
      loaded,
      fontLoadStatus,
    } = state.talkViewer;
    return {
      talkHistory: sceneInfoMap[sceneId].talkHistory,
      viewingMode,
      nextSceneId: sceneMap[sceneId].nextSceneId,
      prevSceneId: sceneMap[sceneId].prevSceneId,
      talkMap,
      sceneMap,
      talkGroupMap,
      productInfo: productInfoMap[currentProductId!],
      fullscreen,
      isViewerLoaded: loaded,
      isViewerEndExposed: state.viewerEnd.isExposed,
      currentProductId,
      fontLoadStatus,
    };
  });
  const {
    progress: {visitedTalkCount},
    sceneIds,
  } = productInfo;

  const isFullscreen = !!fullscreen;
  const talkIds = useMemo(() => {
    const talkGroupIds = flatten(
      sceneIds.map(sceneId => sceneMap[sceneId].talkGroupIds)
    );
    return flatten(
      talkGroupIds.map(talkGroupId => talkGroupMap[talkGroupId].talkIds)
    );
  }, [sceneIds, sceneMap, talkGroupMap]);
  const talksRef = useRef<{[key: string]: HTMLDivElement}>({});
  const dispatch = useDispatch();

  // 화면 내 노출 톡을 저장하는 함수
  const calculateAndDispatchExposedArea = useMemo(
    () =>
      debounce(() => {
        if (
          !talkHistory ||
          isViewerEndExposed ||
          // 웹폰트 로딩 후에는 텍스트들이 재배치되면서 컴포넌트의 크기가 변할 수 있다. 즉 스크롤이 정확하지 않은 위치로 갈 수 있어서,
          // 웹폰트 로딩 이전에는 뷰포트 내 노출 톡을 계산하지 않는다.
          fontLoadStatus === FontLoadStatus.BeforeLoad
        ) {
          return;
        }
        const windowInnerHeight = window.innerHeight;
        const topVisibleOffset = window.pageYOffset;
        let firstTalkIdInView: TalkKey | undefined;
        let lastTalkIdInView: TalkKey | undefined;
        let firstTalkIdChecked = false;
        talkHistory.some(talkId => {
          const rect = talksRef.current[talkId].getBoundingClientRect();
          if (rect.bottom >= windowInnerHeight) {
            // 이 경우는 톡 하나가 위아래로 화면을 초과해서 제대로 마지막 톡을 찾지 못한 경우이다.
            // 그래서 이 때는 해당 톡을 맨 위, 아래톡에 할당한다.
            if (!lastTalkIdInView && !firstTalkIdInView) {
              firstTalkIdInView = talkId;
              lastTalkIdInView = talkId;
            }
            return true;
          }
          if (rect.bottom <= 0) {
            return false;
          }
          // 스크롤모드에서 가상 톡은 기준 톡으로 잡지 않는다.
          if (
            viewingMode === ViewingMode.Scroll &&
            talkMap[talkId].datatype === TalkDataType.None
          ) {
            return false;
          }
          if (!firstTalkIdChecked && rect.bottom > 0) {
            firstTalkIdChecked = true;
            firstTalkIdInView = talkId;
          }

          if (talkMap[talkId].datatype !== TalkDataType.Bgm) {
            lastTalkIdInView = talkId;
          }
          return false;
        });
        if (lastTalkIdInView) {
          dispatch(
            events.productExposedAreaChanged(
              firstTalkIdInView!,
              lastTalkIdInView,
              topVisibleOffset
            )
          );
        }
      }, 100),
    [
      dispatch,
      talkMap,
      talkHistory,
      viewingMode,
      isViewerEndExposed,
      fontLoadStatus,
    ]
  );

  useDisableScrollOnFullscreen(isFullscreen);
  useScrollWhenSceneChange({
    sceneId,
    nextSceneId,
    prevSceneId,
    visitedTalkCount,
    calculateAndDispatchExposedArea,
  });
  useFocusTalk({
    isViewerLoaded,
    isViewerEndExposed,
    fontLoadStatus,
    fullscreen,
    viewingMode,
    calculateAndDispatchExposedArea,
    talkMap,
    currentProductId,
    visitedTalkCount,
    talkElementMap: talksRef.current,
  });

  // 스크롤 변경시마다 노출톡을 재계산합니다.
  useEffect(() => {
    window.addEventListener('scroll', calculateAndDispatchExposedArea);
    return () =>
      window.removeEventListener('scroll', calculateAndDispatchExposedArea);
  }, [calculateAndDispatchExposedArea]);

  const prevViewingMode = usePrevious(viewingMode);
  const prevTalkHistory = usePrevious(talkHistory);
  // 탭모드의 경우 TalkHistory가 변경되면(탭 진행 시) 항상 최하단 스크롤
  useEffect(() => {
    if (
      viewingMode === ViewingMode.Tap &&
      prevViewingMode === ViewingMode.Tap &&
      !isFullscreen &&
      !isEqual(talkHistory, prevTalkHistory) &&
      !isViewerEndExposed
    ) {
      scrollToWindowBottommost();
    }
  }, [
    talkHistory,
    viewingMode,
    isFullscreen,
    isViewerEndExposed,
    prevViewingMode,
    prevTalkHistory,
  ]);

  // 모드변환 시 화면 내 노출 톡 한번 더 확인
  useEffect(() => {
    if (viewingMode !== prevViewingMode) {
      // 탭모드로 변경시 항상 스크롤 아래로
      if (viewingMode === ViewingMode.Tap && !isViewerEndExposed) {
        scrollToWindowBottommost();
      }
      calculateAndDispatchExposedArea();
    }
  }, [
    viewingMode,
    prevViewingMode,
    calculateAndDispatchExposedArea,
    isViewerEndExposed,
  ]);

  // 뷰어 최초 로딩 직후 화면 내 노출 톡 한번 확인
  useEffect(() => {
    if (isViewerLoaded && !productInfo.exposed) {
      calculateAndDispatchExposedArea();
    }
  }, [isViewerLoaded, calculateAndDispatchExposedArea, productInfo.exposed]);

  // 기존 forwardRef로 element를 가져오는 방식은 매번 새로운 render를 유발하기 때문에 element를 담는 함수를 만들어 이를 사용하는 것으로 변경
  const setTalkEl = useCallback((element: HTMLDivElement, talkId: string) => {
    if (element) {
      talksRef.current[talkId] = element;
    }
  }, []);

  const talks = useMemo(() => {
    return talkIds.map(talkId => {
      return (
        <TalkViewerTalkContainer
          key={talkId}
          talkId={talkId}
          setTalkEl={setTalkEl}
          isShowingTalk={!!talkHistory?.includes(talkId) && !isViewerEndExposed}
        />
      );
    });
  }, [talkIds, talkHistory, setTalkEl, isViewerEndExposed]);

  return <>{talks}</>;
}
